import {
  replaceIdMiddleware,
  titleMiddleware,
} from '@blocksuite/affine-shared/adapters';
import { sha } from '@blocksuite/global/utils';
import type { DocSnapshot, Schema, Store, Workspace } from '@blocksuite/store';
import { extMimeMap, getAssetName, Transformer } from '@blocksuite/store';
import {
  createServerDatabaseRootDocId,
  createServerDatabaseRowShardDocId,
  DATABASE_ROW_SHARD_ROWS_MAP_KEY,
  type DatabaseShardManifest,
  FILE_INDEX_MANAGED_BY,
  isFileIndexDatabaseId,
  isHiddenSystemManagedDatabase,
  normalizeDatabaseShardManifest,
} from '@streamlain/database-identity';
import {
  upgradeLegacyDocSnapshot,
  upgradeLegacyYDocArchive,
} from '@streamlain/ydoc-archive-upgrader';
import * as Y from 'yjs';

import {
  createStreamlainIdTracker,
  remapStreamlainReferences,
} from './streamlain-docid-middleware.js';
import { download, StreamingZip, Unzip, Zip } from './utils.js';

type YDocArchiveEntry = {
  readonly sourceDocId: string;
  readonly ydoc: Y.Doc;
};

type YDocArchiveDatabaseSystemDocAccessBinding = {
  readonly databaseId: string;
  readonly rootDocId: string;
  readonly accessDocId: string;
  readonly blockId: string;
  readonly manifest: DatabaseShardManifest;
};

type YDocArchiveImportTarget = {
  readonly sourceDocId: string;
  readonly targetDocId: string;
  readonly system: boolean;
  readonly blockIdReplacements: readonly {
    readonly sourceBlockId: string;
    readonly targetBlockId: string;
  }[];
  readonly databaseSystemDocAccess: readonly YDocArchiveDatabaseSystemDocAccessBinding[];
};

type ZipImportOptions = {
  /**
   * Called after archive doc IDs are remapped, but before target docs are
   * loaded and attached to live sync.
   */
  readonly beforeYDocArchiveDocsLoaded?: (
    targets: readonly YDocArchiveImportTarget[]
  ) => Promise<void>;
  /**
   * Load non-system page docs without adding them to document metadata.
   * Marketplace append imports use this for temporary source docs that are
   * copied into an already existing target doc and discarded immediately after.
   */
  readonly transientPageDocs?: boolean;
  readonly onYDocArchiveProgress?: (progress: {
    readonly processedDocs: number;
    readonly totalDocs: number;
  }) => void;
};

type BlobColumnIds = {
  readonly attachmentColumnIds: ReadonlySet<string>;
  readonly imageColumnIds: ReadonlySet<string>;
};

type BlobColumnLookup = ReadonlyMap<string, BlobColumnIds>;

const SERVER_DATABASE_ROOT_DOC_ID = /^db\$([^$]+)\$database\$([^$]+)\$root$/;
const LOCAL_DATABASE_ROOT_DOC_ID = /^db\$database\$([^$]+)\$root$/;
const SERVER_DATABASE_ROW_SHARD_DOC_ID =
  /^db\$([^$]+)\$database\$([^$]+)\$rows\$([^$]+)$/;
const DATABASE_INLINE_CELLS_PROP = 'prop:cells';
const SURFACE_ELEMENTS_PROP = 'prop:elements';
const INITIAL_TARGET_FOLDER_ID_META_KEY = 'streamlain:initialTargetFolderId';

export function writeArchiveDocumentTitle(
  ydoc: Y.Doc,
  title: string | undefined
): void {
  if (typeof title === 'string' && title.trim()) {
    ydoc.getMap('meta').set('title', title);
  }
}

export function restoreArchiveDocumentTitle(
  meta: { setDocMeta(id: string, props: { title: string }): void },
  docId: string,
  ydoc: Y.Doc
): void {
  const title = ydoc.getMap('meta').get('title');
  if (typeof title === 'string' && title.trim()) {
    meta.setDocMeta(docId, { title });
  }
}

function readArchiveDocId(path: string): string | null {
  const match = /^docs\/(.+)\.ydoc$/.exec(path);
  return match?.[1] ?? null;
}

export function removeSourceWorkspacePlacement(ydoc: Y.Doc): void {
  ydoc.getMap('meta').delete(INITIAL_TARGET_FOLDER_ID_META_KEY);
}

function isArchiveDirectory(path: string): boolean {
  return path.endsWith('/');
}

function isSystemArchiveDocId(docId: string): boolean {
  return docId.startsWith('db$');
}

function registerArchiveDoc(collection: Workspace, docId: string) {
  const registerDoc = (collection as { registerDoc?: (id: string) => void })
    .registerDoc;
  if (registerDoc) {
    registerDoc.call(collection, docId);
    const registeredDoc = collection.getDoc(docId);
    if (!registeredDoc) {
      throw new Error(`Failed to register system archive doc "${docId}".`);
    }
    return registeredDoc;
  }

  return null;
}

function createArchiveDoc(
  collection: Workspace,
  docId: string,
  options: { transientPageDoc?: boolean } = {}
) {
  if (isSystemArchiveDocId(docId)) {
    return registerArchiveDoc(collection, docId) ?? collection.createDoc(docId);
  }

  if (options.transientPageDoc) {
    const transientDoc = registerArchiveDoc(collection, docId);
    if (!transientDoc) {
      throw new Error(
        `Cannot load transient archive doc "${docId}" without registerDoc support.`
      );
    }
    return transientDoc;
  }

  return collection.createDoc(docId);
}

function isYMap(value: unknown): value is Y.Map<unknown> {
  return value instanceof Y.Map;
}

function isYArray(value: unknown): value is Y.Array<unknown> {
  return value instanceof Y.Array;
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return (
    !!value &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    !(value instanceof Y.AbstractType)
  );
}

function cloneYValue(value: unknown): unknown {
  if (value instanceof Y.AbstractType) {
    return value.clone();
  }
  if (Array.isArray(value)) {
    return value.map(cloneYValue);
  }
  if (value && typeof value === 'object') {
    return { ...(value as Record<string, unknown>) };
  }
  return value;
}

function cloneYMap(source: Y.Map<unknown>) {
  const target = new Y.Map<unknown>();
  source.forEach((value, key) => {
    target.set(key, cloneYValue(value));
  });
  return target;
}

function parseDatabaseRootDocId(docId: string): { databaseId: string } | null {
  const server = SERVER_DATABASE_ROOT_DOC_ID.exec(docId);
  if (server) return { databaseId: server[2] };

  const local = LOCAL_DATABASE_ROOT_DOC_ID.exec(docId);
  if (local) return { databaseId: local[1] };

  return null;
}

function parseDatabaseRowShardDocId(
  docId: string
): { databaseId: string; shardId: string } | null {
  const server = SERVER_DATABASE_ROW_SHARD_DOC_ID.exec(docId);
  if (server) return { databaseId: server[2], shardId: server[3] };

  return null;
}

function createTargetDatabaseRootDocId(
  collection: Workspace,
  databaseId: string
): string {
  return createServerDatabaseRootDocId({
    workspaceId: collection.id,
    databaseId,
  });
}

function remapStringField(
  block: Y.Map<unknown>,
  key: string,
  map: ReadonlyMap<string, string>
) {
  const value = block.get(key);
  if (typeof value !== 'string') return;
  const mapped = map.get(value);
  if (mapped) block.set(key, mapped);
}

function remapChildrenArray(
  children: unknown,
  blockIdMap: ReadonlyMap<string, string>
) {
  if (!isYArray(children)) return;

  for (let index = 0; index < children.length; index++) {
    const value = children.get(index);
    if (typeof value !== 'string') continue;

    const mapped = blockIdMap.get(value);
    if (!mapped || mapped === value) continue;

    children.delete(index, 1);
    children.insert(index, [mapped]);
  }
}

function remapAutomationTables(
  value: unknown,
  docIdMap: ReadonlyMap<string, string>,
  blockIdMap: ReadonlyMap<string, string>
) {
  if (!isYArray(value)) return;

  value.forEach(item => {
    if (!isYMap(item)) return;
    remapStringField(item, 'docId', docIdMap);
    remapStringField(item, 'tableId', blockIdMap);
    remapStringField(item, 'blockId', blockIdMap);
  });
}

function remapYMapArrayStringField(
  value: unknown,
  key: string,
  map: ReadonlyMap<string, string>
) {
  if (!isYArray(value)) return;

  value.forEach(item => {
    if (!isYMap(item)) return;
    remapStringField(item, key, map);
  });
}

function remapConnectionEndpoint(
  endpoint: unknown,
  blockIdMap: ReadonlyMap<string, string>
): unknown {
  if (isYMap(endpoint)) {
    remapStringField(endpoint, 'id', blockIdMap);
    return endpoint;
  }

  if (!isPlainRecord(endpoint) || typeof endpoint.id !== 'string') {
    return endpoint;
  }

  const mapped = blockIdMap.get(endpoint.id);
  return mapped ? { ...endpoint, id: mapped } : endpoint;
}

function remapMindmapChildValue(
  value: unknown,
  blockIdMap: ReadonlyMap<string, string>
): unknown {
  if (isYMap(value)) {
    remapStringField(value, 'parent', blockIdMap);
    return value;
  }

  if (!isPlainRecord(value) || typeof value.parent !== 'string') {
    return value;
  }

  const mappedParent = blockIdMap.get(value.parent);
  return mappedParent ? { ...value, parent: mappedParent } : value;
}

function remapSurfaceElementChildren(
  children: unknown,
  blockIdMap: ReadonlyMap<string, string>,
  remapParent: boolean
): unknown {
  if (isYMap(children)) {
    const entries = Array.from(children.entries());
    for (const [sourceId, value] of entries) {
      const targetId = blockIdMap.get(sourceId) ?? sourceId;
      const targetValue = remapParent
        ? remapMindmapChildValue(value, blockIdMap)
        : value;

      if (targetId === sourceId && targetValue === value) continue;

      children.delete(sourceId);
      children.set(targetId, targetValue);
    }
    return children;
  }

  if (!isPlainRecord(children)) {
    return children;
  }

  let changed = false;
  const remapped: Record<string, unknown> = {};
  for (const [sourceId, value] of Object.entries(children)) {
    const targetId = blockIdMap.get(sourceId) ?? sourceId;
    const targetValue = remapParent
      ? remapMindmapChildValue(value, blockIdMap)
      : value;
    remapped[targetId] = targetValue;
    changed ||= targetId !== sourceId || targetValue !== value;
  }

  return changed ? remapped : children;
}

function readElementProp(element: unknown, key: string): unknown {
  if (isYMap(element)) return element.get(key);
  if (isPlainRecord(element)) return element[key];
  return undefined;
}

function writeElementProp(element: unknown, key: string, value: unknown): void {
  if (isYMap(element)) {
    element.set(key, value);
  } else if (isPlainRecord(element)) {
    element[key] = value;
  }
}

function remapSurfaceElement(
  element: unknown,
  blockIdMap: ReadonlyMap<string, string>
): void {
  const type = readElementProp(element, 'type');

  if (type === 'connector') {
    const source = readElementProp(element, 'source');
    const target = readElementProp(element, 'target');
    const remappedSource = remapConnectionEndpoint(source, blockIdMap);
    const remappedTarget = remapConnectionEndpoint(target, blockIdMap);

    if (remappedSource !== source) {
      writeElementProp(element, 'source', remappedSource);
    }
    if (remappedTarget !== target) {
      writeElementProp(element, 'target', remappedTarget);
    }
    return;
  }

  if (type === 'group' || type === 'mindmap') {
    const children = readElementProp(element, 'children');
    const remappedChildren = remapSurfaceElementChildren(
      children,
      blockIdMap,
      type === 'mindmap'
    );
    if (remappedChildren !== children) {
      writeElementProp(element, 'children', remappedChildren);
    }
  }
}

function remapSurfaceElements(
  value: unknown,
  blockIdMap: ReadonlyMap<string, string>
): void {
  const elements = isYMap(value)
    ? value.get('value')
    : isPlainRecord(value)
      ? value.value
      : undefined;

  if (isYMap(elements)) {
    elements.forEach(element => remapSurfaceElement(element, blockIdMap));
    return;
  }

  if (isPlainRecord(elements)) {
    Object.values(elements).forEach(element =>
      remapSurfaceElement(element, blockIdMap)
    );
  }
}

function remapRelationColumns(
  columns: unknown,
  docIdMap: ReadonlyMap<string, string>,
  blockIdMap: ReadonlyMap<string, string>
) {
  if (!isYArray(columns)) return;

  columns.forEach(item => {
    if (!isYMap(item) || item.get('type') !== 'relation') return;

    const data = item.get('data');
    if (!isYMap(data)) return;

    remapStringField(data, 'targetDocId', docIdMap);
    remapStringField(data, 'targetDatabaseId', blockIdMap);
    remapStringField(data, 'targetBlockId', blockIdMap);
    remapStringField(data, 'blockId', blockIdMap);
  });
}

function remapDatabaseManifest(
  collection: Workspace,
  block: Y.Map<unknown>,
  databaseId: string,
  databaseRootDocId: string
) {
  const source = normalizeDatabaseShardManifest(
    block.get('prop:databaseShardManifest')
  );
  if (!source) {
    return;
  }

  block.set('prop:databaseShardManifest', {
    ...source,
    databaseId,
    databaseRootDocId,
    workspaceId: collection.id,
    shards: source.shards.map(shard => ({
      ...shard,
      serverDocId: createServerDatabaseRowShardDocId({
        workspaceId: collection.id,
        databaseId,
        shardId: shard.shardId,
      }),
    })),
  });
}

function readYString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function collectYDatabaseBlocks(ydoc: Y.Doc): [string, Y.Map<unknown>][] {
  const blocks = ydoc.getMap<unknown>('blocks');
  const databaseBlocks: [string, Y.Map<unknown>][] = [];
  blocks.forEach((block, blockId) => {
    if (!isYMap(block) || block.get('sys:flavour') !== 'affine:database') {
      return;
    }
    databaseBlocks.push([blockId, block]);
  });
  return databaseBlocks;
}

function collectPreLoadDatabaseSystemDocAccessBindings(
  entries: readonly YDocArchiveEntry[],
  docIdMap: ReadonlyMap<string, string>
): ReadonlyMap<string, YDocArchiveDatabaseSystemDocAccessBinding[]> {
  const rootsByDatabaseId = new Map<
    string,
    { rootDocId: string; manifest: DatabaseShardManifest }
  >();

  for (const entry of entries) {
    const targetDocId = docIdMap.get(entry.sourceDocId);
    if (!targetDocId || !isSystemArchiveDocId(targetDocId)) continue;

    for (const [, block] of collectYDatabaseBlocks(entry.ydoc)) {
      const databaseId = readYString(block.get('prop:databaseId'));
      const manifest = normalizeDatabaseShardManifest(
        block.get('prop:databaseShardManifest')
      );
      if (!databaseId || !manifest) continue;

      rootsByDatabaseId.set(databaseId, {
        rootDocId:
          readYString(block.get('prop:databaseRootDocId')) ??
          manifest.databaseRootDocId,
        manifest,
      });
    }
  }

  const bindingsByAccessDocId = new Map<
    string,
    YDocArchiveDatabaseSystemDocAccessBinding[]
  >();

  for (const entry of entries) {
    const accessDocId = docIdMap.get(entry.sourceDocId);
    if (!accessDocId || isSystemArchiveDocId(accessDocId)) continue;

    for (const [blockId, block] of collectYDatabaseBlocks(entry.ydoc)) {
      const databaseId = readYString(block.get('prop:databaseId'));
      const rootDocId = readYString(block.get('prop:databaseRootDocId'));
      if (!databaseId || !rootDocId) continue;

      const root = rootsByDatabaseId.get(databaseId);
      if (!root || root.rootDocId !== rootDocId) continue;

      const bindings = bindingsByAccessDocId.get(accessDocId) ?? [];
      bindings.push({
        databaseId,
        rootDocId,
        accessDocId,
        blockId,
        manifest: root.manifest,
      });
      bindingsByAccessDocId.set(accessDocId, bindings);
    }
  }

  return bindingsByAccessDocId;
}

function collectBlockIds(
  collection: Workspace,
  entries: readonly YDocArchiveEntry[]
) {
  const blockIdMap = new Map<string, string>();

  for (const entry of entries) {
    const blocks = entry.ydoc.getMap<unknown>('blocks');
    blocks.forEach((_block, blockId) => {
      if (!blockIdMap.has(blockId)) {
        blockIdMap.set(blockId, collection.idGenerator());
      }
    });
  }

  return blockIdMap;
}

function buildDocIdMap(
  collection: Workspace,
  entries: readonly YDocArchiveEntry[],
  blockIdMap: ReadonlyMap<string, string>
) {
  const docIdMap = new Map<string, string>();

  for (const entry of entries) {
    const root = parseDatabaseRootDocId(entry.sourceDocId);
    if (root) {
      const databaseId = blockIdMap.get(root.databaseId);
      if (!databaseId) {
        throw new Error(
          `Database root doc "${entry.sourceDocId}" has no remapped database block "${root.databaseId}".`
        );
      }

      docIdMap.set(
        entry.sourceDocId,
        createTargetDatabaseRootDocId(collection, databaseId)
      );
      continue;
    }

    const rowShard = parseDatabaseRowShardDocId(entry.sourceDocId);
    if (rowShard) {
      const databaseId = blockIdMap.get(rowShard.databaseId);
      if (!databaseId) {
        throw new Error(
          `Database row shard doc "${entry.sourceDocId}" has no remapped database block "${rowShard.databaseId}".`
        );
      }

      docIdMap.set(
        entry.sourceDocId,
        createServerDatabaseRowShardDocId({
          workspaceId: collection.id,
          databaseId,
          shardId: rowShard.shardId,
        })
      );
      continue;
    }

    docIdMap.set(entry.sourceDocId, collection.idGenerator());
  }

  return docIdMap;
}

function remapYDocBlocks(
  collection: Workspace,
  ydoc: Y.Doc,
  docIdMap: ReadonlyMap<string, string>,
  blockIdMap: ReadonlyMap<string, string>
) {
  const blocks = ydoc.getMap<unknown>('blocks');
  const entries = Array.from(blocks.entries());

  for (const [sourceBlockId, block] of entries) {
    if (!isYMap(block)) continue;

    const targetBlockId = blockIdMap.get(sourceBlockId);
    if (!targetBlockId) {
      throw new Error(`Block "${sourceBlockId}" has no import id mapping.`);
    }

    const targetBlock =
      targetBlockId === sourceBlockId ? block : cloneYMap(block);
    if (targetBlockId !== sourceBlockId) {
      blocks.set(targetBlockId, targetBlock);
      blocks.delete(sourceBlockId);
    }

    targetBlock.set('sys:id', targetBlockId);
    remapChildrenArray(targetBlock.get('sys:children'), blockIdMap);

    const flavour = targetBlock.get('sys:flavour');
    if (flavour === 'affine:automation') {
      remapStringField(targetBlock, 'prop:sourceDocId', docIdMap);
      remapStringField(targetBlock, 'prop:sourceTableId', blockIdMap);
      remapAutomationTables(
        targetBlock.get('prop:inputTables'),
        docIdMap,
        blockIdMap
      );
      remapAutomationTables(
        targetBlock.get('prop:outputTables'),
        docIdMap,
        blockIdMap
      );
      remapYMapArrayStringField(
        targetBlock.get('prop:outputMappings'),
        'tableId',
        blockIdMap
      );
      remapYMapArrayStringField(
        targetBlock.get('prop:matchConfigs'),
        'targetTableId',
        blockIdMap
      );
    }

    if (flavour === 'affine:ghost') {
      remapStringField(targetBlock, 'prop:targetDocId', docIdMap);
    }

    if (flavour === 'affine:database') {
      const databaseId = targetBlockId;
      const databaseRootDocId = createTargetDatabaseRootDocId(
        collection,
        databaseId
      );
      targetBlock.set('prop:databaseId', databaseId);
      if (targetBlock.has('prop:databaseRootDocId')) {
        targetBlock.set('prop:databaseRootDocId', databaseRootDocId);
      }
      remapDatabaseManifest(
        collection,
        targetBlock,
        databaseId,
        databaseRootDocId
      );
      remapRelationColumns(
        targetBlock.get('prop:columns'),
        docIdMap,
        blockIdMap
      );
    }

    if (flavour === 'affine:surface') {
      remapSurfaceElements(targetBlock.get(SURFACE_ELEMENTS_PROP), blockIdMap);
    }
  }
}

async function readYDocArchiveEntry(
  sourceDocId: string,
  blob: Blob
): Promise<YDocArchiveEntry> {
  const ydoc = new Y.Doc({ guid: sourceDocId });
  Y.applyUpdate(ydoc, new Uint8Array(await blob.arrayBuffer()));
  return { sourceDocId, ydoc };
}

async function importYDocArchiveEntries(
  collection: Workspace,
  blobs: readonly [string, Blob][],
  options: ZipImportOptions = {}
): Promise<Store[]> {
  if (blobs.length === 0) return [];

  const entries = await Promise.all(
    blobs.map(([sourceDocId, blob]) => readYDocArchiveEntry(sourceDocId, blob))
  );
  options.onYDocArchiveProgress?.({
    processedDocs: 0,
    totalDocs: entries.length,
  });
  const blockIdMap = collectBlockIds(collection, entries);
  const docIdMap = buildDocIdMap(collection, entries, blockIdMap);
  const imported: Store[] = [];

  for (const entry of entries) {
    const targetDocId = docIdMap.get(entry.sourceDocId);
    if (!targetDocId) {
      throw new Error(`Archive doc "${entry.sourceDocId}" has no id mapping.`);
    }
    entry.ydoc.transact(() => {
      // Folder hierarchy is imported through the archive's explicit folder
      // metadata. A creation-time hint embedded in a source YDoc belongs to
      // the source workspace and must be removed before the YDoc can be
      // attached to the target sync engine.
      removeSourceWorkspacePlacement(entry.ydoc);
      // Prune before remapping: legacy file-index blocks may only be
      // identifiable by their source block/database id.
      pruneImportedFileIndexSystemDatabases(entry.ydoc);
      remapYDocBlocks(collection, entry.ydoc, docIdMap, blockIdMap);
      upgradeLegacyYDocArchive({
        docId: targetDocId,
        source: 'zip-import',
        sourceDocId: entry.sourceDocId,
        systemDoc: isSystemArchiveDocId(targetDocId),
        ydoc: entry.ydoc,
      });
    });
  }

  const databaseSystemDocAccessByAccessDocId =
    collectPreLoadDatabaseSystemDocAccessBindings(entries, docIdMap);
  const blockIdReplacements = Array.from(
    blockIdMap,
    ([sourceBlockId, targetBlockId]) => ({
      sourceBlockId,
      targetBlockId,
    })
  );

  await options.beforeYDocArchiveDocsLoaded?.(
    entries.map(entry => {
      const targetDocId = docIdMap.get(entry.sourceDocId);
      if (!targetDocId) {
        throw new Error(
          `Archive doc "${entry.sourceDocId}" has no id mapping.`
        );
      }
      return {
        sourceDocId: entry.sourceDocId,
        targetDocId,
        system: isSystemArchiveDocId(targetDocId),
        blockIdReplacements,
        databaseSystemDocAccess:
          databaseSystemDocAccessByAccessDocId.get(targetDocId) ?? [],
      };
    })
  );

  for (const [index, entry] of entries.entries()) {
    const targetDocId = docIdMap.get(entry.sourceDocId);
    if (!targetDocId) {
      throw new Error(`Archive doc "${entry.sourceDocId}" has no id mapping.`);
    }

    const targetDoc = createArchiveDoc(collection, targetDocId, {
      transientPageDoc:
        options.transientPageDocs && !isSystemArchiveDocId(targetDocId),
    });
    targetDoc.load();
    Y.applyUpdate(targetDoc.spaceDoc, Y.encodeStateAsUpdate(entry.ydoc));
    if (!options.transientPageDocs && !isSystemArchiveDocId(targetDocId)) {
      restoreArchiveDocumentTitle(collection.meta, targetDocId, entry.ydoc);
    }
    imported.push(targetDoc.getStore({ id: targetDocId }));
    options.onYDocArchiveProgress?.({
      processedDocs: index + 1,
      totalDocs: entries.length,
    });
  }

  return imported;
}

function yDocUpdateBlob(update: Uint8Array): Blob {
  return new Blob([
    update.buffer.slice(
      update.byteOffset,
      update.byteOffset + update.byteLength
    ) as ArrayBuffer,
  ]);
}

function cloneStoreYDocForArchive(store: Store): Y.Doc {
  const ydoc = new Y.Doc({ guid: store.id });
  Y.applyUpdate(ydoc, Y.encodeStateAsUpdate(store.spaceDoc));
  writeArchiveDocumentTitle(ydoc, store.meta?.title);
  pruneLegacyInlineDatabases(ydoc);
  pruneImportedFileIndexSystemDatabases(ydoc);
  return ydoc;
}

function isLegacyInlineDatabaseBlock(block: Y.Map<unknown>): boolean {
  return (
    block.get('sys:flavour') === 'affine:database' &&
    !block.has('prop:databaseRootDocId') &&
    !block.has('prop:databaseShardManifest') &&
    !block.has('prop:databaseStorageVersion')
  );
}

function isFileIndexSystemDatabaseBlock(block: Y.Map<unknown>): boolean {
  const databaseId = block.get('prop:databaseId');
  const blockId = block.get('sys:id');
  const fileIndexId =
    typeof databaseId === 'string'
      ? databaseId
      : typeof blockId === 'string'
        ? blockId
        : undefined;
  return (
    block.get('sys:flavour') === 'affine:database' &&
    block.get('prop:visible') !== true &&
    (block.get('prop:managedBy') === FILE_INDEX_MANAGED_BY ||
      isFileIndexDatabaseId(fileIndexId))
  );
}

function isDocScopedSystemDatabaseBlock(block: {
  id?: string;
  props: {
    databaseId?: unknown;
    docScoped?: unknown;
    managedBy?: unknown;
    systemManaged?: unknown;
    visible?: unknown;
  };
}): boolean {
  const databaseId =
    typeof block.props.databaseId === 'string'
      ? block.props.databaseId
      : block.id;
  return (
    isHiddenSystemManagedDatabase({
      id: databaseId,
      managedBy: block.props.managedBy,
      visible: block.props.visible,
    }) ||
    (block.props.systemManaged === true && block.props.docScoped === true)
  );
}

function collectBlockSubtree(
  blocks: Y.Map<unknown>,
  blockId: string,
  ids: Set<string>
): void {
  if (ids.has(blockId)) return;
  ids.add(blockId);

  const block = blocks.get(blockId);
  if (!isYMap(block)) return;

  const children = block.get('sys:children');
  if (!isYArray(children)) return;

  children.forEach(childId => {
    if (typeof childId === 'string') {
      collectBlockSubtree(blocks, childId, ids);
    }
  });
}

function removeChildrenFromParent(
  children: unknown,
  idsToRemove: ReadonlySet<string>
): void {
  if (!isYArray(children)) return;

  for (let index = children.length - 1; index >= 0; index--) {
    const childId = children.get(index);
    if (typeof childId === 'string' && idsToRemove.has(childId)) {
      children.delete(index, 1);
    }
  }
}

function pruneLegacyInlineDatabases(ydoc: Y.Doc): void {
  const blocks = ydoc.getMap<unknown>('blocks');
  const idsToRemove = new Set<string>();

  blocks.forEach((block, blockId) => {
    if (isYMap(block) && isLegacyInlineDatabaseBlock(block)) {
      collectBlockSubtree(blocks, blockId, idsToRemove);
    }
  });

  if (idsToRemove.size === 0) return;

  ydoc.transact(() => {
    blocks.forEach(block => {
      if (isYMap(block)) {
        removeChildrenFromParent(block.get('sys:children'), idsToRemove);
      }
    });
    idsToRemove.forEach(blockId => blocks.delete(blockId));
  });
}

export function pruneImportedFileIndexSystemDatabases(ydoc: Y.Doc): void {
  const blocks = ydoc.getMap<unknown>('blocks');
  const idsToRemove = new Set<string>();

  blocks.forEach((block, blockId) => {
    if (isYMap(block) && isFileIndexSystemDatabaseBlock(block)) {
      collectBlockSubtree(blocks, blockId, idsToRemove);
    }
  });

  if (idsToRemove.size === 0) return;

  ydoc.transact(() => {
    blocks.forEach(block => {
      if (isYMap(block)) {
        removeChildrenFromParent(block.get('sys:children'), idsToRemove);
      }
    });
    idsToRemove.forEach(blockId => blocks.delete(blockId));
  });
}

function getStoreForExistingDoc(
  collection: Workspace,
  docId: string,
  owner: string
): Store {
  const doc = collection.getDoc(docId);
  if (!doc) {
    throw new Error(
      `Cannot export sharded database "${owner}" because document "${docId}" is not available.`
    );
  }
  if (!doc.ready) {
    doc.load();
  }
  return doc.getStore({ id: docId });
}

function addStore(stores: Store[], seen: Set<string>, store: Store): void {
  if (seen.has(store.id)) return;
  seen.add(store.id);
  stores.push(store);
}

function walkStoreBlocks(
  store: Store,
  visit: (block: NonNullable<Store['root']>) => void
): void {
  const walk = (block: NonNullable<Store['root']>): void => {
    visit(block);
    block.children.forEach(child => walk(child));
  };

  if (store.root) {
    walk(store.root);
  }
}

function collectReferencedDatabaseRootDocIds(store: Store): string[] {
  const rootDocIds = new Set<string>();

  walkStoreBlocks(store, block => {
    if (block.flavour !== 'affine:database') return;
    if (isDocScopedSystemDatabaseBlock(block)) {
      return;
    }

    const rootDocId = (block.props as { databaseRootDocId?: unknown })
      .databaseRootDocId;
    if (typeof rootDocId === 'string' && rootDocId) {
      rootDocIds.add(rootDocId);
    }
  });

  return [...rootDocIds];
}

function collectDatabaseShardManifests(store: Store): DatabaseShardManifest[] {
  const manifests: DatabaseShardManifest[] = [];

  walkStoreBlocks(store, block => {
    if (block.flavour !== 'affine:database') return;
    if (isDocScopedSystemDatabaseBlock(block)) {
      return;
    }

    const manifest = normalizeDatabaseShardManifest(
      (block.props as { databaseShardManifest?: unknown }).databaseShardManifest
    );
    if (manifest) {
      manifests.push(manifest);
    }
  });

  return manifests;
}

function collectYDocArchiveStores(
  collection: Workspace,
  docs: readonly Store[]
): Store[] {
  const stores: Store[] = [];
  const seen = new Set<string>();

  docs.forEach(doc => addStore(stores, seen, doc));

  let index = 0;
  while (index < stores.length) {
    const store = stores[index]!;
    index++;

    for (const rootDocId of collectReferencedDatabaseRootDocIds(store)) {
      addStore(
        stores,
        seen,
        getStoreForExistingDoc(collection, rootDocId, store.id)
      );
    }

    for (const manifest of collectDatabaseShardManifests(store)) {
      for (const shard of manifest.shards) {
        const existingDocId = collection.getDoc(shard.serverDocId)
          ? shard.serverDocId
          : null;
        if (!existingDocId) {
          throw new Error(
            `Cannot export sharded database "${manifest.databaseId}" because row shard "${shard.shardId}" is not available.`
          );
        }
        addStore(
          stores,
          seen,
          getStoreForExistingDoc(collection, existingDocId, manifest.databaseId)
        );
      }
    }
  }

  return stores;
}

function readYValue(value: unknown): unknown {
  if (isYMap(value)) {
    const record: Record<string, unknown> = {};
    value.forEach((item, key) => {
      record[key] = readYValue(item);
    });
    return record;
  }

  if (isYArray(value)) {
    return value.toArray().map(readYValue);
  }

  if (value instanceof Y.Text) {
    return value.toString();
  }

  if (Array.isArray(value)) {
    return value.map(readYValue);
  }

  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, item]) => [
        key,
        readYValue(item),
      ])
    );
  }

  return value;
}

function addBlobId(blobIds: Set<string>, value: unknown): void {
  if (typeof value !== 'string') return;
  const blobId = value.replace(/^\//, '');
  if (blobId) {
    blobIds.add(blobId);
  }
}

function collectBlobIdsFromDatabaseCell(
  blobIds: Set<string>,
  columnType: string,
  cell: unknown
): void {
  const value =
    cell && typeof cell === 'object' && !Array.isArray(cell) && 'value' in cell
      ? (cell as { value?: unknown }).value
      : cell;

  if (columnType === 'image') {
    addBlobId(blobIds, value);
    return;
  }

  if (
    columnType === 'attachment' &&
    value &&
    typeof value === 'object' &&
    !Array.isArray(value)
  ) {
    Object.keys(value as Record<string, unknown>).forEach(blobId =>
      addBlobId(blobIds, blobId)
    );
  }
}

function readDatabaseBlobColumnIds(block: Y.Map<unknown>) {
  const attachmentColumnIds = new Set<string>();
  const imageColumnIds = new Set<string>();
  const columns = readYValue(block.get('prop:columns'));

  if (Array.isArray(columns)) {
    columns.forEach(column => {
      if (!column || typeof column !== 'object' || Array.isArray(column)) {
        return;
      }
      const { id, type } = column as { id?: unknown; type?: unknown };
      if (typeof id !== 'string' || typeof type !== 'string') return;

      if (type === 'attachment') {
        attachmentColumnIds.add(id);
      } else if (type === 'image') {
        imageColumnIds.add(id);
      }
    });
  }

  return { attachmentColumnIds, imageColumnIds };
}

function collectDatabaseCellBlobIds(
  blobIds: Set<string>,
  cells: unknown,
  columns: {
    readonly attachmentColumnIds: ReadonlySet<string>;
    readonly imageColumnIds: ReadonlySet<string>;
  }
): void {
  const rows = readYValue(cells);
  if (!rows || typeof rows !== 'object' || Array.isArray(rows)) return;

  Object.values(rows as Record<string, unknown>).forEach(row => {
    if (!row || typeof row !== 'object' || Array.isArray(row)) return;

    const rowCells = row as Record<string, unknown>;
    columns.attachmentColumnIds.forEach(columnId =>
      collectBlobIdsFromDatabaseCell(blobIds, 'attachment', rowCells[columnId])
    );
    columns.imageColumnIds.forEach(columnId =>
      collectBlobIdsFromDatabaseCell(blobIds, 'image', rowCells[columnId])
    );
  });
}

function collectBlobColumnLookup(
  entries: readonly YDocArchiveEntry[]
): BlobColumnLookup {
  const lookup = new Map<
    string,
    {
      readonly attachmentColumnIds: ReadonlySet<string>;
      readonly imageColumnIds: ReadonlySet<string>;
    }
  >();

  entries.forEach(entry => {
    const blocks = entry.ydoc.getMap<unknown>('blocks');
    blocks.forEach((block, blockId) => {
      if (!isYMap(block) || block.get('sys:flavour') !== 'affine:database') {
        return;
      }

      const columns = readDatabaseBlobColumnIds(block);
      const databaseId = block.get('prop:databaseId');
      lookup.set(blockId, columns);
      if (typeof databaseId === 'string' && databaseId) {
        lookup.set(databaseId, columns);
      }
    });
  });

  return lookup;
}

function collectYDocArchiveBlobIds(
  entries: readonly YDocArchiveEntry[],
  inheritedBlobColumnLookup?: Map<string, BlobColumnIds>
): Set<string> {
  const blobIds = new Set<string>();
  const blobColumnLookup =
    inheritedBlobColumnLookup ?? new Map<string, BlobColumnIds>();
  for (const [key, columns] of collectBlobColumnLookup(entries)) {
    blobColumnLookup.set(key, columns);
  }

  entries.forEach(entry => {
    const blocks = entry.ydoc.getMap<unknown>('blocks');
    blocks.forEach(block => {
      if (!isYMap(block)) return;

      addBlobId(blobIds, block.get('prop:sourceId'));

      if (block.get('sys:flavour') !== 'affine:database') return;

      const columns = readDatabaseBlobColumnIds(block);
      collectDatabaseCellBlobIds(
        blobIds,
        block.get(DATABASE_INLINE_CELLS_PROP),
        columns
      );
    });

    const rowShard = parseDatabaseRowShardDocId(entry.sourceDocId);
    if (!rowShard) return;

    const columns = blobColumnLookup.get(rowShard.databaseId);
    if (!columns) return;

    collectDatabaseCellBlobIds(
      blobIds,
      entry.ydoc.getMap(DATABASE_ROW_SHARD_ROWS_MAP_KEY),
      columns
    );
  });

  return blobIds;
}

function getAssetExtension(blob: Blob): string {
  for (const [ext, mime] of extMimeMap) {
    if (mime === blob.type) return ext;
  }
  return 'bin';
}

async function writeYDocArchiveAssets(
  zip: Zip,
  collection: Workspace,
  blobIds: ReadonlySet<string>,
  excludeBlobIds?: ReadonlySet<string>
): Promise<void> {
  const assets = zip.folder('assets');
  await Promise.all(
    [...blobIds]
      .filter(blobId => !excludeBlobIds?.has(blobId))
      .map(async blobId => {
        const blob = await collection.blobSync.get(blobId);
        if (!blob) {
          throw new Error(
            `Cannot export referenced blob "${blobId}" because it is not available.`
          );
        }
        await assets.file(`${blobId}.${getAssetExtension(blob)}`, blob);
      })
  );
}

async function exportYDocArchive(
  collection: Workspace,
  docs: readonly Store[],
  excludeBlobIds?: Set<string>
) {
  const stores = collectYDocArchiveStores(collection, docs);
  const entries = stores.map(store => ({
    sourceDocId: store.id,
    ydoc: cloneStoreYDocForArchive(store),
  }));
  const zip = new Zip();

  await Promise.all(
    entries.map(entry =>
      zip.file(
        `docs/${entry.sourceDocId}.ydoc`,
        yDocUpdateBlob(Y.encodeStateAsUpdate(entry.ydoc))
      )
    )
  );

  await writeYDocArchiveAssets(
    zip,
    collection,
    collectYDocArchiveBlobIds(entries),
    excludeBlobIds
  );

  const downloadBlob = await zip.generate();
  return download(downloadBlob, `${collection.id}.bs.zip`);
}

export type WorkspaceArchiveProgress =
  | { phase: 'documents'; processedDocs: number }
  | { phase: 'assets'; processedAssets: number }
  | { phase: 'finalizing' };

/**
 * Incremental workspace archive writer. Callers retain only one loaded document
 * family at a time; compressed output is drained to the supplied sink before
 * the next family is accepted.
 */
export class WorkspaceYDocArchiveWriter {
  private readonly familyBlobColumnLookup = new Map<string, BlobColumnIds>();
  private readonly zip: StreamingZip;
  private processedDocs = 0;
  private processedAssets = 0;

  constructor(
    private readonly collection: Workspace,
    sink: (chunk: Uint8Array) => Promise<void>,
    private readonly options: {
      excludeBlobIds?: ReadonlySet<string>;
      claimBlobId?: (id: string) => Promise<boolean>;
      claimDocId?: (id: string) => Promise<boolean>;
      signal?: AbortSignal;
      onProgress?: (progress: WorkspaceArchiveProgress) => void;
    } = {}
  ) {
    this.zip = new StreamingZip(sink);
  }

  async appendFamily(stores: readonly Store[]): Promise<void> {
    this.throwIfAborted();
    for (const store of stores) {
      if (
        this.options.claimDocId &&
        !(await this.options.claimDocId(store.id))
      ) {
        continue;
      }
      const entry: YDocArchiveEntry = {
        sourceDocId: store.id,
        ydoc: cloneStoreYDocForArchive(store),
      };
      try {
        this.throwIfAborted();
        await this.zip.file(
          `docs/${entry.sourceDocId}.ydoc`,
          yDocUpdateBlob(Y.encodeStateAsUpdate(entry.ydoc))
        );
        this.processedDocs += 1;
        this.options.onProgress?.({
          phase: 'documents',
          processedDocs: this.processedDocs,
        });
        for (const blobId of collectYDocArchiveBlobIds(
          [entry],
          this.familyBlobColumnLookup
        )) {
          if (this.options.excludeBlobIds?.has(blobId)) continue;
          if (
            this.options.claimBlobId &&
            !(await this.options.claimBlobId(blobId))
          ) {
            continue;
          }
          this.throwIfAborted();
          const blob = await this.collection.blobSync.get(blobId);
          if (!blob) {
            throw new Error(
              `Cannot export referenced blob "${blobId}" because it is not available.`
            );
          }
          await this.zip.file(
            `assets/${blobId}.${getAssetExtension(blob)}`,
            blob
          );
          this.processedAssets += 1;
          this.options.onProgress?.({
            phase: 'assets',
            processedAssets: this.processedAssets,
          });
        }
      } finally {
        entry.ydoc.destroy();
      }
    }
  }

  finishFamily(): void {
    this.familyBlobColumnLookup.clear();
  }

  async close(): Promise<void> {
    this.options.onProgress?.({ phase: 'finalizing' });
    await this.zip.generate();
  }

  private throwIfAborted(): void {
    this.options.signal?.throwIfAborted();
  }
}

async function exportDocs(
  collection: Workspace,
  schema: Schema,
  docs: Store[],
  excludeBlobIds?: Set<string>
) {
  if (docs.length === 0) {
    throw new Error('Snapshot export requires at least one document.');
  }

  if (requiresYDocArchiveExport(docs)) {
    return exportYDocArchive(collection, docs, excludeBlobIds);
  }

  const zip = new Zip();
  const job = new Transformer({
    schema,
    blobCRUD: collection.blobSync,
    docCRUD: {
      create: (id: string) => collection.createDoc(id).getStore({ id }),
      get: (id: string) => collection.getDoc(id)?.getStore({ id }) ?? null,
      delete: (id: string) => collection.removeDoc(id),
    },
    middlewares: [
      replaceIdMiddleware(collection.idGenerator),
      titleMiddleware(collection.meta.docMetas),
    ],
  });
  const snapshotResults = await Promise.all(
    docs.map(async doc => ({
      doc,
      snapshot: job.docToSnapshot(doc),
    }))
  );
  const failedDocs = snapshotResults.filter(result => !result.snapshot);
  if (failedDocs.length > 0) {
    const labels = failedDocs.map(({ doc }) => doc.meta?.title || doc.id);
    throw new Error(
      `Snapshot export failed for document(s): ${labels.join(', ')}`
    );
  }
  const snapshots = snapshotResults.map(result => result.snapshot!);

  await Promise.all(
    snapshots.map(async snapshot => {
      // Use the title and id as the snapshot file name
      const title = snapshot.meta.title || 'untitled';
      const id = snapshot.meta.id;
      const snapshotName = `${title}-${id}.snapshot.json`;
      await zip.file(snapshotName, JSON.stringify(snapshot, null, 2));
    })
  );

  const assets = zip.folder('assets');
  const pathBlobIdMap = job.assetsManager.getPathBlobIdMap();
  const assetsMap = job.assets;

  // Add blobs to assets folder, if failed, log the error and continue
  // sl-ht07.5: Filter out excluded blobs (user deselected in attachment dialog)
  const blobIdsToExport = excludeBlobIds?.size
    ? Array.from(pathBlobIdMap.values()).filter(id => !excludeBlobIds.has(id))
    : Array.from(pathBlobIdMap.values());

  const results = await Promise.all(
    blobIdsToExport.map(async blobId => {
      try {
        await job.assetsManager.readFromBlob(blobId);
        const ext = getAssetName(assetsMap, blobId).split('.').at(-1);
        const blob = assetsMap.get(blobId);
        if (blob) {
          await assets.file(`${blobId}.${ext}`, blob);
          return { success: true, blobId };
        }
        return { success: false, blobId, error: 'Blob not found' };
      } catch (error) {
        console.error(`Failed to process blob: ${blobId}`, error);
        return { success: false, blobId, error };
      }
    })
  );

  const failures = results.filter(r => !r.success);
  if (failures.length > 0) {
    console.warn(`Failed to process ${failures.length} blobs:`, failures);
  }

  const downloadBlob = await zip.generate();
  // Use the collection id as the zip file name
  return download(downloadBlob, `${collection.id}.bs.zip`);
}

function requiresYDocArchiveExport(docs: readonly Store[]): boolean {
  return docs.some(
    doc =>
      collectReferencedDatabaseRootDocIds(doc).length > 0 ||
      collectDatabaseShardManifests(doc).length > 0
  );
}

async function writeYDocArchiveAssetBlobs(
  collection: Workspace,
  assetBlobs: readonly [string, Blob][]
): Promise<void> {
  await Promise.all(
    assetBlobs.map(async ([name, blob]) => {
      const nameWithExt = name.replace('assets/', '');
      const assetId = nameWithExt.replace(/\.[^/.]+$/, '');
      const ext = nameWithExt.split('.').at(-1) ?? '';
      const mime = extMimeMap.get(ext) ?? blob.type;
      const file = new File([blob], nameWithExt, { type: mime });
      await collection.blobSync.set(assetId, file);
    })
  );
}

async function importDocs(
  collection: Workspace,
  schema: Schema,
  imported: Blob,
  options: ZipImportOptions = {}
) {
  const unzip = new Unzip();
  await unzip.load(imported);

  const assetBlobs: [string, Blob][] = [];
  const snapshotsBlobs: Blob[] = [];
  const ydocBlobs: [string, Blob][] = [];

  for (const { path, content: blob } of unzip) {
    if (path.includes('MACOSX') || path.includes('DS_Store')) {
      continue;
    }

    if (isArchiveDirectory(path)) {
      continue;
    }

    if (path.startsWith('assets/')) {
      assetBlobs.push([path, blob]);
      continue;
    }

    if (path === 'info.json') {
      continue;
    }

    if (path.endsWith('.snapshot.json')) {
      snapshotsBlobs.push(blob);
      continue;
    }

    const archiveDocId = readArchiveDocId(path);
    if (archiveDocId) {
      ydocBlobs.push([archiveDocId, blob]);
      continue;
    }
  }

  if (snapshotsBlobs.length > 0 && ydocBlobs.length > 0) {
    throw new Error(
      'ZIP import cannot mix snapshot JSON docs and docs/*.ydoc archive docs.'
    );
  }

  if (ydocBlobs.length > 0) {
    await writeYDocArchiveAssetBlobs(collection, assetBlobs);
  }

  // sl-ht07.3: Track old→new ID mappings for StreamLain-specific props
  const docIdMap = new Map<string, string>();
  const blockIdMap = new Map<string, string>();

  const job = new Transformer({
    schema,
    blobCRUD: collection.blobSync,
    docCRUD: {
      create: (id: string) => collection.createDoc(id).getStore({ id }),
      get: (id: string) => collection.getDoc(id)?.getStore({ id }) ?? null,
      delete: (id: string) => collection.removeDoc(id),
    },
    middlewares: [
      // ID tracker MUST be first to capture original IDs before replaceIdMiddleware mutates them
      createStreamlainIdTracker(docIdMap, blockIdMap),
      replaceIdMiddleware(collection.idGenerator),
      titleMiddleware(collection.meta.docMetas),
    ],
  });
  const assetsMap = job.assets;

  assetBlobs.forEach(([name, blob]) => {
    const nameWithExt = name.replace('assets/', '');
    const assetsId = nameWithExt.replace(/\.[^/.]+$/, '');
    const ext = nameWithExt.split('.').at(-1) ?? '';
    const mime = extMimeMap.get(ext) ?? '';
    const file = new File([blob], nameWithExt, {
      type: mime,
    });
    assetsMap.set(assetsId, file);
  });

  const snapshotStores = await Promise.all(
    snapshotsBlobs.map(async blob => {
      const json = await blob.text();
      const snapshot = JSON.parse(json) as DocSnapshot;
      upgradeLegacyDocSnapshot({
        docId: snapshot.meta.id,
        snapshot,
        source: 'zip-snapshot-import',
        sourceDocId: snapshot.meta.id,
      });
      const tasks: Promise<void>[] = [];

      job.walk(snapshot, block => {
        const sourceId = block.props?.sourceId as string | undefined;

        if (sourceId && sourceId.startsWith('/')) {
          const removeSlashId = sourceId.replace(/^\//, '');

          if (assetsMap.has(removeSlashId)) {
            const blob = assetsMap.get(removeSlashId)!;

            tasks.push(
              blob
                .arrayBuffer()
                .then(buffer => sha(buffer))
                .then(hash => {
                  assetsMap.set(hash, blob);
                  block.props.sourceId = hash;
                })
            );
          }
        }
      });

      await Promise.all(tasks);

      return job.snapshotToDoc(snapshot);
    })
  );
  const ydocStores = await importYDocArchiveEntries(
    collection,
    ydocBlobs,
    options
  );

  // sl-ht07.3: Post-import fixup — remap docId/blockId references in
  // Automation, Ghost, and Database (relation column) blocks
  const stores = [...snapshotStores, ...ydocStores];
  const validStores = stores.filter((s): s is Store => !!s);
  remapStreamlainReferences(validStores, docIdMap, blockIdMap);

  return stores;
}

export const ZipTransformer = {
  exportDocs,
  importDocs,
};
